import chroma from 'chroma-js';

export const parseColor = (color?: string) => {
  if (!color) {
    return undefined;
  }
  try {
    return chroma(color);
  } catch (e) {
    return undefined;
  }
};

export const isWhite = (color?: string) => {
  if (!color) return false;
  try {
    return chroma(color).hex().toLowerCase() === '#ffffff';
  } catch (e) {
    return false;
  }
};

/**
 * Determines if a color is light or dark based on the HSP color model.
 * @param r red channel value 0-255
 * @param g green channel value 0-255
 * @param g blue channel value 0-255
 * @returns boolean true if the color is in the upper half of the HSP color range.
 */
export const isLightColor = (r: number, g: number, b: number): boolean => {
  const HALF_HSP_RANGE = 127.5;

  // HSP equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Determine whether the color is dark
  if (hsp < HALF_HSP_RANGE) {
    return false;
  }

  return true;
};

export const darkenColor = (color: string) => {
  // taken from monorepo
  return chroma(
    chroma(color)
      .rgb()
      .map(x => x * 0.8),
  ).hex();
};

export const toRgbArray = (color: string): [number, number, number] => {
  return chroma(color).rgb();
};

export const hexToRgb = (hex: string): [number, number, number] => {
  return chroma(hex).rgb();
};

export const isWhiteishHex = (hex: string, threshold = 0.9): boolean => {
  try {
    const luminance = chroma(hex).luminance(); // 0 (black) → 1 (white)
    return luminance >= threshold;
  } catch {
    return false; // invalid color
  }
};

export type ColorRGBA = [r: number, g: number, b: number, a: number];
export type Color = ColorRGBA | string;

const transformToChromaColor = (color: Color): chroma.Color => {
  if (Array.isArray(color)) {
    const [r, g, b, a] = color;
    return chroma(r, g, b, a);
  }
  return chroma(color);
};

export const fadeColor = (color: Color, alpha: number): string => {
  const chromaColor = transformToChromaColor(color);
  return chromaColor.alpha(alpha).css();
};

export const mixColors = (c1: string, pct: number, c2: string): string => {
  try {
    return chroma.mix(c1, c2, (100 - pct) / 100, 'rgb').hex();
  } catch {
    return c2;
  }
};
